import { useSyncExternalStore } from 'react';

/**
 * Auth store — source unique de vérité pour l'authentification.
 *
 * RÈGLE useSyncExternalStore :
 *   getSnapshot() DOIT retourner la même référence si l'état n'a pas changé.
 *   Retourner `{ ..._state }` à chaque appel crée un nouvel objet → boucle
 *   infinie "Maximum update depth exceeded".
 *
 *   Solution : on maintient un snapshot mis en cache (_snap).
 *   Il n'est remplacé QUE dans _notify(), c'est-à-dire quand l'état change vraiment.
 */

const AUTH_KEY = 'madjeo_auth';

function load() {
  try { return JSON.parse(localStorage.getItem(AUTH_KEY) || 'null'); } catch { return null; }
}
function save(s) {
  localStorage.setItem(AUTH_KEY, JSON.stringify(s));
}

const _listeners = new Set();

// État interne mutable (jamais exposé directement)
const _state = {
  user:         load()?.user         ?? null,
  accessToken:  load()?.accessToken  ?? null,
  refreshToken: load()?.refreshToken ?? null,
};

// Snapshot immuable partagé avec React.
// Même référence tant que l'état n'a pas changé → pas de boucle infinie.
let _snap = {
  user:         _state.user,
  accessToken:  _state.accessToken,
  refreshToken: _state.refreshToken,
};

export const useAuthStore = {

  // ── getSnapshot : retourne toujours _snap (référence stable entre deux _notify) ──
  getState: () => _snap,

  // ── subscribe : compatible useSyncExternalStore ───────────────────────────
  subscribe(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
  },

  // ── _notify : crée un NOUVEAU snapshot puis notifie React ────────────────
  _notify() {
    save({ user: _state.user, accessToken: _state.accessToken, refreshToken: _state.refreshToken });
    // Nouvelle référence d'objet → React détecte le changement
    _snap = { user: _state.user, accessToken: _state.accessToken, refreshToken: _state.refreshToken };
    _listeners.forEach(fn => fn());
  },

  // ── Actions métier ────────────────────────────────────────────────────────
  login(user, tokens) {
    _state.user         = user;
    _state.accessToken  = tokens.access  ?? tokens.accessToken  ?? null;
    _state.refreshToken = tokens.refresh ?? tokens.refreshToken ?? null;
    this._notify();
  },

  logout() {
    _state.user         = null;
    _state.accessToken  = null;
    _state.refreshToken = null;
    localStorage.removeItem(AUTH_KEY);
    // Compat legacy tokens
    ['access_token', 'refresh_token', 'auth_token', 'userName']
      .forEach(k => localStorage.removeItem(k));
    this._notify();
  },

  updateUser(updates) {
    _state.user = _state.user ? { ..._state.user, ...updates } : updates;
    this._notify();
  },

  isAuthenticated() {
    return !!(
      _state.accessToken ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('auth_token')
    );
  },
};

// ── Hook React (composants) ───────────────────────────────────
export function useAuth() {
  // subscribe doit être stable → on ne crée pas de nouvelle fonction ici
  const state = useSyncExternalStore(
    useAuthStore.subscribe.bind(useAuthStore),
    useAuthStore.getState,
    // getServerSnapshot (optionnel, SSR) — retourne le même snapshot
    useAuthStore.getState,
  );
  return {
    ...state,
    isAuthenticated: useAuthStore.isAuthenticated(),
    login:           useAuthStore.login.bind(useAuthStore),
    logout:          useAuthStore.logout.bind(useAuthStore),
  };
}
